import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Monitor, Target, Video, Users, Check } from 'lucide-react';

const VIBEFramework = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });

  const vibeItems = [
    {
      letter: 'V',
      title: 'Virtual',
      description: 'Your message deserves a digital stage that feels intentional and professional. We help ministries and creators establish a strong virtual presence through studio builds, live streaming systems, and online production environments.',
      features: [
        'Professional live stream systems',
        'Home or church studio design',
        'Virtual event environments'
      ],
      icon: Monitor
    },
    {
      letter: 'I',
      title: 'Impact',
      description: 'Production should amplify the message, not distract from it. We design systems that allow your message to be heard clearly and experienced with excellence.',
      features: [
        'Clear audio and visual storytelling',
        'Seamless live production',
        'Broadcast-quality delivery'
      ],
      icon: Target
    },
    {
      letter: 'B',
      title: 'Broadcast',
      description: "You don't need a television studio to produce broadcast-level content. We bring the standards of professional broadcasting into your environment.",
      features: [
        'Multi-camera switching',
        'Professional lighting design',
        'Real-time graphics and streaming'
      ],
      icon: Video
    },
    {
      letter: 'E',
      title: 'Experience',
      description: "Production is more than equipment, it's the experience you create for your audience. From the moment someone joins your live stream or watches your content, the experience should feel intentional, smooth, and engaging.",
      features: [
        'Seamless viewer experience',
        'Professional presentation',
        'Consistent delivery'
      ],
      icon: Users
    }
  ];

  return (
    <section id="vibe" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian overflow-hidden" data-testid="vibe-section" ref={ref}>
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neon-blue/30 to-transparent" />
      
      {/* Subtle background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-pink/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-10 sm:mb-12 md:mb-16"
        >
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4 sm:mb-6" data-testid="vibe-heading">
            The <span className="text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}>V.I.B.E.</span> Framework
          </h2>
          
          {/* Decorative line with subtitle */}
          <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8">
            <div className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-amber-400/50" />
            <span className="font-serif italic text-amber-400/90 text-base sm:text-lg md:text-xl">
              Virtual Impact & Broadcast Experience
            </span>
            <div className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-amber-400/50" />
          </div>

          <p className="text-sm sm:text-base md:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
            At Kingdom V.I.B.E. Studios, production isn't just about cameras and streaming. It's about creating a clear, powerful experience that allows your message to reach people with excellence and consistency. That's why every project is guided by our V.I.B.E. framework.
          </p>
        </motion.div>

        {/* V.I.B.E. Cards Grid - 4-column layout, wider cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 mb-12 sm:mb-16">
          {vibeItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="group relative bg-white/[0.03] backdrop-blur-sm border border-white/10 hover:border-amber-400/30 transition-all duration-300 p-4 lg:p-5"
              data-testid={`vibe-card-${item.letter.toLowerCase()}`}
            >
              {/* Header Row - Letter and Title side by side */}
              <div className="flex items-center gap-3 mb-3">
                <span 
                  className="font-heading text-4xl lg:text-5xl font-black text-amber-400 leading-none"
                  style={{ textShadow: '0 0 30px rgba(251, 191, 36, 0.3)' }}
                >
                  {item.letter}
                </span>
                <div>
                  <h3 className="font-heading text-sm lg:text-base font-bold uppercase text-white leading-tight">
                    {item.title}
                  </h3>
                  <div className="flex items-center justify-center w-8 h-8 lg:w-10 lg:h-10 bg-white/5 border border-amber-400/30 mt-1">
                    <item.icon className="w-4 h-4 lg:w-5 lg:h-5 text-amber-400" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-white/50 leading-relaxed mb-3">
                {item.description}
              </p>

              {/* Features with checkmarks - compact */}
              <ul className="space-y-1">
                {item.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-1.5 text-xs text-white/70">
                    <Check className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400/0 via-amber-400/50 to-amber-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          ))}
        </div>

        {/* Decorative gold line */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent mb-8 sm:mb-12" />

        {/* Closing Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <p className="text-sm sm:text-base md:text-lg text-white leading-relaxed mb-4">
            When these four elements work together, your message is no longer limited by technology.
          </p>
          <p 
            className="font-serif italic text-lg sm:text-xl md:text-2xl text-amber-400"
            style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.3)' }}
          >
            It becomes a Virtual Impact & Broadcast Experience.
          </p>
          <p className="text-sm sm:text-base text-white mt-2">
            Excellence in every production.
          </p>
        </motion.div>
      </div>

      {/* Bottom separator line */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neon-blue/30 to-transparent" />
    </section>
  );
};

export default VIBEFramework;
